export type CanonicalOfferCondition = "factory_sealed" | "new" | "refurbished" | "used" | "for_parts";

/** Supplier labels are evidence, not an opportunity to infer an unstated condition. */
export function canonicalOfferCondition(value: string | null | undefined): CanonicalOfferCondition | null {
  const compact = (value ?? "").normalize("NFKD").toLowerCase().replace(/[^a-z0-9]/g, "").replace(/condition$/, "");
  if ([
    "forparts", "forpartsornotworking", "forpartsnotworking", "partsonly", "notworking", "sparesorrepair",
    "damaged", "forrepair", "defective", "faulty", "broken", "defekt", "ersatzteile",
    "furersatzteile", "furersatzteiledefekt", "fuerersatzteile", "fuerersatzteiledefekt",
  ].includes(compact)) return "for_parts";
  if (["factorysealed", "newfactorysealed", "newsealed", "sealed", "newinsealedbox"].includes(compact)) return "factory_sealed";
  if (["refurbished", "refurb", "sellerrefurbished", "manufacturerrefurbished", "certifiedrefurbished", "reconditioned", "remanufactured"].includes(compact)) return "refurbished";
  if (["used", "usedacceptable", "usedgood", "usedverygood", "usedlikenew", "preowned", "secondhand", "usato", "gebraucht"].includes(compact)) return "used";
  if (["new", "brandnew", "newother", "newotherseedetails", "newopenbox", "newinbox", "nuovo", "neu", "nuevo", "neuf"].includes(compact)) return "new";
  return null;
}

export function normalizedOfferCondition(value: string): string {
  return canonicalOfferCondition(value) ?? value.trim().toLowerCase().replace(/[\s-]+/g, "_");
}

/** Multiple distinct accepted states retain unstated offers; one condition requires evidence. */
export function matchesAcceptedOfferConditions(
  value: string | null | undefined,
  accepted: readonly string[] | null | undefined,
  allowUnknown?: boolean,
): boolean {
  if (!accepted?.length) return true;
  const normalized = [...new Set(accepted.map(normalizedOfferCondition).filter(Boolean))];
  if (normalized.includes("any") || normalized.includes("all")) return true;
  const condition = canonicalOfferCondition(value);
  if (condition) return normalized.includes(condition);
  // The dropdown can explicitly select a supplier's raw unstated label. Such
  // labels are not additional confirmed states in an API procurement request.
  if (allowUnknown === false && value && normalized.includes(normalizedOfferCondition(value))) return true;
  const distinctKnownConditions = new Set(accepted.map(canonicalOfferCondition).filter(condition => condition !== null));
  return allowUnknown ?? distinctKnownConditions.size > 1;
}
